import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lightbulb, ExternalLink, ThumbsUp, ThumbsDown, MessageSquare,
  Search, Filter, Plus, CheckCircle, Star, Globe, BookOpen,
  Wrench, Package, FolderOpen, MoreHorizontal, Link2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import GuestBanner from '@/components/GuestBanner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  website: Globe,
  partner: Star,
  reference: BookOpen,
  tool: Wrench,
  product: Package,
  resource: FolderOpen,
  other: MoreHorizontal
};

const CATEGORY_COLORS = {
  website: 'bg-blue-100 text-blue-800',
  partner: 'bg-yellow-100 text-yellow-800',
  reference: 'bg-purple-100 text-purple-800',
  tool: 'bg-green-100 text-green-800',
  product: 'bg-orange-100 text-orange-800',
  resource: 'bg-cyan-100 text-cyan-800',
  other: 'bg-gray-100 text-gray-800'
};

const Recommendations = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userVotes, setUserVotes] = useState({});

  useEffect(() => {
    fetchCategories();
    fetchRecommendations();
  }, [selectedCategory, sortBy]);

  useEffect(() => {
    if (user && recommendations.length > 0) {
      fetchUserVotes();
    }
  }, [user, recommendations]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/recommendations/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/recommendations/?sort_by=${sortBy}`;
      if (selectedCategory && selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const response = await axios.get(url);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserVotes = async () => {
    if (!token) return;
    
    const votes = {};
    for (const rec of recommendations) {
      try {
        const response = await axios.get(
          `${API_URL}/api/recommendations/${rec.id}/vote`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.vote) {
          votes[rec.id] = response.data.vote;
        }
      } catch (error) {
        // Ignore errors for individual vote fetches
      }
    }
    setUserVotes(votes);
  };

  const handleVote = async (recId, voteType) => {
    if (!user) {
      toast.error('Please sign in to vote');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/recommendations/${recId}/vote?vote=${voteType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      
      // Update local state
      if (response.data.action === 'removed') {
        setUserVotes(prev => {
          const newVotes = { ...prev };
          delete newVotes[recId];
          return newVotes;
        });
      } else {
        setUserVotes(prev => ({ ...prev, [recId]: voteType }));
      }
      
      // Refresh recommendations
      fetchRecommendations();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecommendations();
  };

  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || MoreHorizontal;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Guest Banner */}
        {!user && <GuestBanner message="Sign in to share recommendations and vote on resources" />}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2">
              <Lightbulb className="w-8 h-8 text-yellow-500" />
              Community Recommendations
            </h1>
            <p className="text-muted-foreground mt-1">
              Discover and share useful resources, websites, and potential partners
            </p>
          </div>
          {user && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Recommendation
            </Button>
          )}
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search recommendations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-40">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score">Top Rated</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" variant="secondary">
                <Filter className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory('all')}
          >
            All
          </Button>
          {categories.map(cat => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.value)}
              className="flex items-center gap-1"
            >
              {getCategoryIcon(cat.value)}
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Recommendations Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : recommendations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Lightbulb className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No recommendations yet</h3>
              <p className="text-muted-foreground mb-4">
                Be the first to share a useful resource with the community!
              </p>
              {user && (
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Recommendation
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendations.map(rec => (
              <Card key={rec.id} className="flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={CATEGORY_COLORS[rec.category] || CATEGORY_COLORS.other}>
                          {getCategoryIcon(rec.category)}
                          <span className="ml-1">{rec.category}</span>
                        </Badge>
                        {rec.is_verified && (
                          <Badge variant="outline" className="text-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" /> Verified
                          </Badge>
                        )}
                        {rec.is_featured && (
                          <Badge className="bg-yellow-500 text-yellow-900">
                            <Star className="w-3 h-3 mr-1" /> Featured
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg line-clamp-2">{rec.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                    {rec.description}
                  </p>
                  
                  {rec.why_recommend && (
                    <p className="text-sm italic text-muted-foreground mb-3 border-l-2 pl-2">
                      "{rec.why_recommend}"
                    </p>
                  )}

                  {rec.url && (
                    <a 
                      href={rec.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1 mb-3"
                    >
                      <Link2 className="w-3 h-3" />
                      Visit Link
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  {rec.tags && rec.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {rec.tags.slice(0, 3).map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="mt-auto pt-3 border-t">
                    <div className="flex items-center justify-between">
                      {/* Vote Buttons */}
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant={userVotes[rec.id] === 'up' ? 'default' : 'ghost'}
                          onClick={() => handleVote(rec.id, 'up')}
                          className="h-8 px-2"
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          {rec.upvotes}
                        </Button>
                        <Button
                          size="sm"
                          variant={userVotes[rec.id] === 'down' ? 'destructive' : 'ghost'}
                          onClick={() => handleVote(rec.id, 'down')}
                          className="h-8 px-2"
                        >
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          {rec.downvotes}
                        </Button>
                        <Badge variant="outline" className="ml-2">
                          Score: {rec.score}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MessageSquare className="w-4 h-4" />
                        {rec.comments_count}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <Avatar className="w-5 h-5">
                        <AvatarFallback className="text-xs">
                          {(rec.author_display_name || rec.author_username || 'U')[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span>{rec.author_display_name || rec.author_username}</span>
                      <span>·</span>
                      <span>{formatDistanceToNow(new Date(rec.created_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Recommendation Dialog */}
        <AddRecommendationDialog 
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          categories={categories}
          token={token}
          onSuccess={() => {
            setDialogOpen(false);
            fetchRecommendations();
          }}
        />
      </div>
    </div>
  );
};

// Add Recommendation Dialog Component
const AddRecommendationDialog = ({ open, onOpenChange, categories, token, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    category: '',
    tags: '',
    why_recommend: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/recommendations/`,
        {
          ...formData,
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Recommendation added successfully!');
      setFormData({ title: '', url: '', description: '', category: '', tags: '', why_recommend: '' });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add recommendation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            Add Recommendation
          </DialogTitle>
          <DialogDescription>
            Share a useful resource with the Club community
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Best Pet Insurance Comparison Site"
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label>Category *</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label} - {cat.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>URL (optional)</Label>
            <Input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://example.com"
            />
          </div>

          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this resource is and what it offers..."
              rows={3}
              minLength={10}
              maxLength={2000}
            />
          </div>

          <div className="space-y-2">
            <Label>Why do you recommend this? (optional)</Label>
            <Textarea
              value={formData.why_recommend}
              onChange={(e) => setFormData({ ...formData, why_recommend: e.target.value })}
              placeholder="Share your personal experience or why others should check this out..."
              rows={2}
              maxLength={1000}
            />
          </div>

          <div className="space-y-2">
            <Label>Tags (optional, comma-separated)</Label>
            <Input
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="insurance, health, dogs"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Recommendation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Recommendations;
